
<template>
  <div class="task-manager">

    <!-- ===== HEADER ===== -->
    <header class="tm-header">
      <div class="tm-header-left">
        <div class="tm-header-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
          </svg>
        </div>
        <div>
          <h1 class="tm-header-title">Task Manager</h1>
          <p class="tm-header-sub">{{ tasks.length }} tasks · {{ completionPercentage }}% done</p>
        </div>
      </div>
      <button class="tm-btn-primary" @click="openCreateDialog">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        <span class="tm-btn-label">New task</span>
      </button>
    </header>

    <!-- ===== STATS ===== -->
    <div class="tm-stats">
      <div class="tm-stats-grid">
        <div class="tm-stat tm-stat--total" @click="filterStatus='all'; filterTasks()">
          <span class="tm-stat-num">{{ stats[0].value }}</span>
          <span class="tm-stat-label">Total</span>
        </div>
        <div class="tm-stat tm-stat--done" @click="filterStatus='completed'; filterTasks()">
          <span class="tm-stat-num">{{ stats[1].value }}</span>
          <span class="tm-stat-label">Done</span>
        </div>
        <div class="tm-stat tm-stat--review" @click="filterStatus='review'; filterTasks()">
          <span class="tm-stat-num">{{ stats[2].value }}</span>
          <span class="tm-stat-label">Review</span>
        </div>
        <div class="tm-stat tm-stat--pending" @click="filterStatus='pending'; filterTasks()">
          <span class="tm-stat-num">{{ stats[3].value }}</span>
          <span class="tm-stat-label">Pending</span>
        </div>
      </div>
      <div class="tm-progress-wrap">
        <div class="tm-progress-track">
          <div class="tm-progress-fill" :style="{ width: completionPercentage + '%' }" />
        </div>
        <span class="tm-progress-text">{{ completionPercentage }}%</span>
      </div>
    </div>

    <!-- ===== TOOLBAR ===== -->
    <div class="tm-toolbar">
      <div class="tm-search">
        <svg class="tm-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8c959f" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
        <input v-model="searchQuery" class="tm-search-input" placeholder="Search tasks…" type="search" />
      </div>
      <div class="tm-toolbar-right">
        <select v-model="filterStatus" class="tm-select-pill" @change="filterTasks()">
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In progress</option>
          <option value="review">In review</option>
          <option value="completed">Completed</option>
        </select>
        <select v-model="priorityFilter" class="tm-select-pill" @change="filterTasks()">
          <option value="all">Priority</option>
          <option value="high">🔴 High</option>
          <option value="medium">🟡 Medium</option>
          <option value="low">🟢 Low</option>
        </select>
        <select v-model="sortBy" class="tm-select-pill" @change="sortTasks()">
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="priority">Priority</option>
        </select>
      </div>
    </div>

    <!-- ===== TASK LIST ===== -->
    <div class="tm-list" v-if="filteredTasks.length > 0">
      <div v-for="task in filteredTasks" :key="task.id" class="tm-task-wrap">

        <!-- Task row -->
        <div
          class="tm-item"
          :class="['tm-item--' + task.priority, { 'tm-item--done': task.completed, 'tm-item--review': task.status === 'review' }]"
          @click="openTaskDetails(task)"
        >
          <button class="tm-check" :class="{ checked: task.completed }" @click.stop="toggleComplete(task)" :aria-label="task.completed ? 'Mark incomplete' : 'Mark complete'">
            <svg v-if="!task.completed" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#c9d1d9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/></svg>
            <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2da44e" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
          </button>

          <div class="tm-item-body">
            <div class="tm-item-top">
              <span class="tm-item-title" :class="{ 'tm-item-title--done': task.completed }">{{ task.title }}</span>
              <div class="tm-item-pills">
                <span class="tm-pill" :class="'tm-pill--' + (task.status || 'pending')">{{ statusLabel(task.status) }}</span>
                <span v-if="task.priority" class="tm-pill" :class="'tm-pill--' + task.priority">{{ task.priority }}</span>
              </div>
            </div>
            <p v-if="task.description" class="tm-item-desc" :class="{ 'tm-item-desc--done': task.completed }">{{ task.description }}</p>
            <div class="tm-item-foot">
              <span v-if="task.assigned_to" class="tm-foot-chip">
                <span class="tm-foot-avatar">{{ getUserInitial(task.assigned_to) }}</span>
                {{ getUserName(task.assigned_to) }}
              </span>
              <span class="tm-foot-chip tm-foot-chip--time">{{ formatRelativeDate(task.created_at) }}</span>
              <span v-if="task.subtasks && task.subtasks.length" class="tm-foot-chip tm-foot-chip--purple">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 01-9 9"/></svg>
                {{ task.subtasks.length }}
              </span>
              <span v-if="(taskComments[task.id] || []).length > 0" class="tm-foot-chip tm-foot-chip--blue">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>
                {{ (taskComments[task.id] || []).length }}
              </span>
              <span v-if="task.review_links && task.review_links.length" class="tm-foot-chip tm-foot-chip--violet">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                {{ task.review_links.length }} link{{ task.review_links.length > 1 ? 's' : '' }}
              </span>
              <span class="tm-foot-chip tm-foot-chip--green">{{ task.progress || 0 }}%</span>
            </div>
          </div>

          <div class="tm-item-actions" @click.stop>
            <button v-if="!task.completed && task.status !== 'review'" class="tm-action tm-action--violet" @click.stop="openReviewDialog(task)" title="Submit for review">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            </button>
            <button class="tm-action tm-action--blue" @click.stop="openSubtaskDialog(task)" title="Add subtask">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 01-9 9"/></svg>
            </button>
            <button class="tm-action tm-action--edit" @click.stop="openEditDialog(task)" title="Edit">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            </button>
            <button class="tm-action tm-action--del" @click.stop="confirmDelete(task)" title="Delete">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
            </button>
          </div>
        </div>

        <!-- Thread (review links + comments) -->
        <transition name="tm-thread">
          <div v-if="(taskComments[task.id] || []).length > 0 || inlineReply[task.id] || (task.review_links && task.review_links.length)" class="tm-thread" @click.stop>
            <div class="tm-thread-body">

              <!-- Review comment bloc violet -->
              <div v-if="task.review_links && task.review_links.length" class="tm-review-bloc">
                <div class="tm-review-bloc-avatar">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                </div>
                <div class="tm-review-bloc-bubble">
                  <div class="tm-review-bloc-head">
                    <span class="tm-review-bloc-author">{{ getUserName(task.assigned_to) || 'Assignee' }}</span>
                    <span class="tm-review-tag">Submitted for review</span>
                    <span class="tm-review-bloc-date">{{ formatRelativeDate(task.created_at) }}</span>
                  </div>
                  <p class="tm-review-bloc-sub">Pages &amp; links worked on :</p>
                  <div class="tm-review-links-list">
                    <div v-for="link in task.review_links" :key="link" class="tm-review-link-row">
                      <component
                        :is="isExternal(link) ? 'a' : 'router-link'"
                        v-bind="isExternal(link) ? { href: link, target: '_blank', rel: 'noopener noreferrer' } : { to: link }"
                        class="tm-review-link-left"
                        @click.native.stop
                      >
                        <span class="tm-review-link-icon" :class="isExternal(link) ? 'tm-review-link-icon--ext' : ''">
                          <svg v-if="isExternal(link)" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                          <svg v-else width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                        </span>
                        <span class="tm-review-link-path">{{ link }}</span>
                        <span v-if="isExternal(link)" class="tm-ext-badge">ext</span>
                      </component>
                      <div class="tm-review-link-actions">
                        <component
                          :is="isExternal(link) ? 'a' : 'router-link'"
                          v-bind="isExternal(link) ? { href: link, target: '_blank', rel: 'noopener noreferrer' } : { to: link }"
                          class="tm-view-btn"
                          @click.native.stop
                        >
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                          View
                        </component>
                        <button class="tm-del-link-btn" @click.stop="removeReviewLink(task, link)" title="Remove">
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                      </div>
                    </div>
                  </div>
                  <button class="tm-add-link-inline-btn" @click.stop="openReviewDialog(task)">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                    Add link or page
                  </button>
                </div>
              </div>

              <!-- Commentaires -->
              <div v-for="c in taskComments[task.id] || []" :key="c.id" class="tm-msg">
                <div class="tm-msg-avatar">{{ (c.author || 'U').charAt(0).toUpperCase() }}</div>
                <div class="tm-msg-bubble">
                  <div class="tm-msg-head"><strong>{{ c.author }}</strong><span class="tm-msg-date">{{ formatRelativeDate(c.created_at) }}</span></div>
                  <p class="tm-msg-text">{{ c.content }}</p>
                </div>
              </div>

              <!-- Reply input -->
              <div class="tm-reply">
                <div class="tm-msg-avatar tm-msg-avatar--me">{{ (currentUser || 'M').charAt(0).toUpperCase() }}</div>
                <div class="tm-reply-wrap">
                  <textarea v-model="inlineReply[task.id]" class="tm-reply-input" placeholder="Write a comment…" rows="2" @keydown.enter.ctrl.prevent="sendInlineComment(task.id)"></textarea>
                  <div class="tm-reply-foot">
                    <span class="tm-reply-hint">Ctrl+Enter to send</span>
                    <button class="tm-btn-primary tm-btn-primary--xs" :disabled="!inlineReply[task.id] || !inlineReply[task.id].trim() || sendingInline[task.id]" @click="sendInlineComment(task.id)">
                      <span v-if="sendingInline[task.id]" class="tm-spin">⟳</span>
                      <span v-else>Send</span>
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </transition>
      </div>
    </div>

    <!-- ===== EMPTY ===== -->
    <div v-else class="tm-empty">
      <div class="tm-empty-icon">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 11l3 3L22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
      </div>
      <div class="tm-empty-title">{{ searchQuery || filterStatus !== 'all' || priorityFilter !== 'all' ? 'No matching tasks' : 'No tasks yet' }}</div>
      <div class="tm-empty-sub">{{ searchQuery || filterStatus !== 'all' || priorityFilter !== 'all' ? 'Try adjusting your filters' : 'Create your first task to get started' }}</div>
      <button v-if="!searchQuery && filterStatus === 'all' && priorityFilter === 'all'" class="tm-btn-primary tm-btn-primary--sm" @click="openCreateDialog">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
        New task
      </button>
    </div>

    <!-- ════ DIALOG Create/Edit ════ -->
    <transition name="tm-fade">
      <div v-if="dialog" class="tm-overlay" @click.self="closeDialog">
        <div class="tm-modal" role="dialog">
          <div class="tm-modal-header">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="margin-right:8px;opacity:.8">
              <path v-if="editingTask" d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path v-if="editingTask" d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
              <line v-else x1="12" y1="5" x2="12" y2="19"/><line v-else x1="5" y1="12" x2="19" y2="12"/>
            </svg>
            {{ editingTask ? 'Edit task' : 'New task' }}
            <button class="tm-modal-close" @click="closeDialog"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          </div>
          <div class="tm-modal-body">
            <label class="tm-label">Title <span class="tm-req">*</span></label>
            <input v-model="taskForm.title" class="tm-input" placeholder="What needs to be done?" />
            <label class="tm-label tm-label--mt">Description</label>
            <textarea v-model="taskForm.description" class="tm-input tm-textarea" placeholder="Add details…" rows="3" />
            <div class="tm-form-row">
              <div class="tm-form-col">
                <label class="tm-label">Priority</label>
                <select v-model="taskForm.priority" class="tm-input tm-sel"><option value="high">🔴 High</option><option value="medium">🟡 Medium</option><option value="low">🟢 Low</option></select>
              </div>
              <div class="tm-form-col">
                <label class="tm-label">Assign to</label>
                <select v-model="taskForm.assigned_to" class="tm-input tm-sel">
                  <option :value="null">Unassigned</option>
                  <option v-for="u in users" :key="u.id" :value="u.id">{{ u.username }}</option>
                </select>
              </div>
            </div>
          </div>
          <div class="tm-modal-footer">
            <button class="tm-btn-ghost" @click="closeDialog">Cancel</button>
            <button class="tm-btn-primary" :disabled="!taskForm.title || saving" @click="saveTask">
              <span v-if="saving" class="tm-spin">⟳</span><span v-else>{{ editingTask ? 'Update' : 'Create' }}</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ════ DIALOG Delete ════ -->
    <transition name="tm-fade">
      <div v-if="deleteDialog" class="tm-overlay" @click.self="deleteDialog = false">
        <div class="tm-modal tm-modal--sm" role="alertdialog">
          <div class="tm-modal-header tm-modal-header--danger">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="margin-right:8px"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
            Delete task
            <button class="tm-modal-close" @click="deleteDialog = false"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          </div>
          <div class="tm-modal-body">
            <div class="tm-delete-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#cf222e" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
            </div>
            <p class="tm-delete-msg">Delete <strong>"{{ taskToDelete ? taskToDelete.title : '' }}"</strong>? This cannot be undone.</p>
          </div>
          <div class="tm-modal-footer">
            <button class="tm-btn-ghost" @click="deleteDialog = false">Cancel</button>
            <button class="tm-btn-danger" :disabled="deleting" @click="deleteTask"><span v-if="deleting" class="tm-spin">⟳</span><span v-else>Delete</span></button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ════ DIALOG Subtask ════ -->
    <transition name="tm-fade">
      <div v-if="subtaskDialog" class="tm-overlay" @click.self="subtaskDialog = false">
        <div class="tm-modal tm-modal--sm" role="dialog">
          <div class="tm-modal-header">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:8px"><line x1="6" y1="3" x2="6" y2="15"/><circle cx="18" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><path d="M18 9a9 9 0 01-9 9"/></svg>
            <span class="tm-modal-trunc">Subtask — {{ subtaskParent ? subtaskParent.title : '' }}</span>
            <button class="tm-modal-close" @click="subtaskDialog = false"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          </div>
          <div class="tm-modal-body">
            <label class="tm-label">Title <span class="tm-req">*</span></label>
            <input v-model="subtaskForm.title" class="tm-input" placeholder="Subtask title" />
            <label class="tm-label tm-label--mt">Description</label>
            <textarea v-model="subtaskForm.description" class="tm-input tm-textarea" placeholder="Details…" rows="2" />
            <div class="tm-form-row">
              <div class="tm-form-col">
                <label class="tm-label">Priority</label>
                <select v-model="subtaskForm.priority" class="tm-input tm-sel"><option value="high">🔴 High</option><option value="medium">🟡 Medium</option><option value="low">🟢 Low</option></select>
              </div>
              <div class="tm-form-col">
                <label class="tm-label">Assign to</label>
                <select v-model="subtaskForm.assigned_to" class="tm-input tm-sel">
                  <option :value="null">Unassigned</option>
                  <option v-for="u in users" :key="u.id" :value="u.id">{{ u.username }}</option>
                </select>
              </div>
            </div>
          </div>
          <div class="tm-modal-footer">
            <button class="tm-btn-ghost" @click="subtaskDialog = false">Cancel</button>
            <button class="tm-btn-primary" :disabled="!subtaskForm.title || savingSubtask" @click="createSubtask"><span v-if="savingSubtask" class="tm-spin">⟳</span><span v-else>Create</span></button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ════ DIALOG Review ════ -->
    <transition name="tm-fade">
      <div v-if="reviewDialog" class="tm-overlay" @click.self="reviewDialog = false">
        <div class="tm-modal tm-modal--md" role="dialog">
          <div class="tm-modal-header tm-modal-header--violet">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:8px"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <span class="tm-modal-trunc">Submit for review — {{ reviewTask ? reviewTask.title : '' }}</span>
            <button class="tm-modal-close" @click="reviewDialog = false"><svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          </div>

          <div class="tm-modal-body">

            <!-- Tabs -->
            <div class="tm-review-tabs">
              <button class="tm-review-tab" :class="{ active: reviewTab === 'routes' }" @click="reviewTab = 'routes'">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                App pages
              </button>
              <button class="tm-review-tab" :class="{ active: reviewTab === 'custom' }" @click="reviewTab = 'custom'">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                Custom URL
              </button>
            </div>

            <!-- Tab: App pages -->
            <div v-show="reviewTab === 'routes'">
              <div class="tm-rsearch">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8c959f" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                <input v-model="routeSearch" class="tm-rsearch-input" placeholder="Search a page…" type="search" autocomplete="off" />
                <button v-if="routeSearch" class="tm-rsearch-clear" @click="routeSearch = ''"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
              </div>
              <div class="tm-route-list">
                <div v-if="filteredRoutes.length === 0" class="tm-route-empty">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#d0d7de" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                  No pages match "{{ routeSearch }}"
                </div>
                <label v-for="route in filteredRoutes" :key="route" class="tm-route-row" :class="{ 'tm-route-row--on': selectedRoutes.includes(route) }">
                  <input type="checkbox" :value="route" v-model="selectedRoutes" class="tm-route-cb" />
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0;opacity:.5"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                  <span>{{ route }}</span>
                </label>
              </div>
            </div>

            <!-- Tab: Custom URL -->
            <div v-show="reviewTab === 'custom'" class="tm-custom-url-panel">
              <p class="tm-custom-url-hint">Paste any URL from another app, staging environment or external tool.</p>
              <div class="tm-custom-url-row">
                <div class="tm-custom-url-input-wrap">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#8c959f" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  <input
                    v-model="customUrl"
                    class="tm-custom-url-input"
                    placeholder="https://staging.myapp.com/dashboard"
                    type="url"
                    @keydown.enter.prevent="addCustomUrl"
                  />
                </div>
                <button class="tm-btn-violet" :disabled="!customUrl.trim()" @click="addCustomUrl">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add
                </button>
              </div>
              <div v-if="customUrls.length" class="tm-custom-url-list">
                <div v-for="url in customUrls" :key="url" class="tm-custom-url-item">
                  <span class="tm-custom-url-ext-badge">ext</span>
                  <span class="tm-custom-url-text">{{ url }}</span>
                  <button class="tm-custom-url-remove" @click="customUrls = customUrls.filter(u => u !== url)">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </div>
              </div>
              <div v-else class="tm-custom-url-empty">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#d0d7de" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                <span>No custom URLs added yet</span>
              </div>
            </div>

            <!-- Selected summary chips -->
            <div v-if="allSelectedLinks.length" class="tm-selected-summary">
              <div class="tm-selected-summary-label">{{ allSelectedLinks.length }} link{{ allSelectedLinks.length > 1 ? 's' : '' }} selected</div>
              <div class="tm-chips">
                <span v-for="r in allSelectedLinks" :key="r" class="tm-chip">
                  <span class="tm-chip-ext" v-if="isExternal(r)">ext</span>
                  {{ r.length > 40 ? r.slice(0, 40) + '…' : r }}
                  <button class="tm-chip-x" @click="removeFromSelection(r)">
                    <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                  </button>
                </span>
              </div>
            </div>
          </div>

          <div class="tm-modal-footer">
            <button class="tm-btn-ghost" @click="reviewDialog = false">Cancel</button>
            <button class="tm-btn-violet-solid" @click="markReview(reviewTask)" :disabled="submittingReview || allSelectedLinks.length === 0">
              <span v-if="submittingReview" class="tm-spin">⟳</span>
              <span v-else>Submit ({{ allSelectedLinks.length }})</span>
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- ════ DRAWER Detail ════ -->
    <transition name="tm-slide">
      <div v-if="detailsDrawer" class="tm-overlay tm-overlay--drawer" @click.self="detailsDrawer = false">
        <div class="tm-drawer" role="dialog">
          <div class="tm-drawer-handle" />
          <div class="tm-drawer-header">
            <span class="tm-drawer-title">Details</span>
            <button class="tm-drawer-close" @click="detailsDrawer = false"><svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          </div>
          <div class="tm-drawer-body" v-if="selectedTask">
            <div class="tm-drawer-pills">
              <span class="tm-pill" :class="'tm-pill--' + (selectedTask.status || 'pending')">{{ statusLabel(selectedTask.status) }}</span>
              <span class="tm-pill" :class="'tm-pill--' + (selectedTask.priority || 'none')">{{ selectedTask.priority || 'None' }}</span>
            </div>
            <h2 class="tm-drawer-title2">{{ selectedTask.title }}</h2>
            <p v-if="selectedTask.description" class="tm-drawer-desc">{{ selectedTask.description }}</p>

            <div class="tm-detail-grid">
              <div class="tm-detail-cell">
                <span class="tm-detail-label">Assigned</span>
                <span class="tm-detail-val">{{ getUserName(selectedTask.assigned_to) || '—' }}</span>
              </div>
              <div class="tm-detail-cell">
                <span class="tm-detail-label">Progress</span>
                <span class="tm-detail-val">{{ selectedTask.progress || 0 }}% <button class="tm-ai-btn" @click="fetchAIProgress(selectedTask)">AI</button></span>
              </div>
              <div class="tm-detail-cell">
                <span class="tm-detail-label">Created</span>
                <span class="tm-detail-val">{{ formatRelativeDate(selectedTask.created_at) }}</span>
              </div>
              <div v-if="selectedTask.completed_at" class="tm-detail-cell">
                <span class="tm-detail-label">Completed</span>
                <span class="tm-detail-val">{{ formatRelativeDate(selectedTask.completed_at) }}</span>
              </div>
              <div v-if="selectedTask.parent" class="tm-detail-cell">
                <span class="tm-detail-label">Parent</span>
                <span class="tm-detail-val">{{ selectedTask.parent.title }}</span>
              </div>
            </div>

            <div v-if="selectedTask.subtasks && selectedTask.subtasks.length" class="tm-section">
              <div class="tm-section-label">Subtasks</div>
              <ul class="tm-subtask-list">
                <li v-for="st in selectedTask.subtasks" :key="st.id">
                  <span :class="{ 'tm-strike': st.completed }">{{ st.title }}</span>
                  <span class="tm-pill" :class="'tm-pill--' + st.status" style="font-size:10px;margin-left:6px">{{ st.status }}</span>
                </li>
              </ul>
            </div>

            <!-- Comments + review links -->
            <div class="tm-comments">
              <div class="tm-comments-head">
                <span class="tm-section-label">Activity</span>
                <span class="tm-comments-count">{{ (taskComments[selectedTask.id] || []).length + (selectedTask.review_links && selectedTask.review_links.length ? 1 : 0) }}</span>
              </div>

              <!-- Review bloc violet -->
              <div v-if="selectedTask.review_links && selectedTask.review_links.length" class="tm-review-comment">
                <div class="tm-review-comment-head">
                  <div class="tm-review-comment-ava">
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                  </div>
                  <strong>{{ getUserName(selectedTask.assigned_to) || 'Assignee' }}</strong>
                  <span class="tm-review-tag">Submitted for review</span>
                  <span class="tm-msg-date">{{ formatRelativeDate(selectedTask.created_at) }}</span>
                </div>
                <p class="tm-review-comment-sub">Pages &amp; links worked on :</p>
                <div class="tm-review-links-list">
                  <div v-for="link in selectedTask.review_links" :key="link" class="tm-review-link-row">
                    <component
                      :is="isExternal(link) ? 'a' : 'router-link'"
                      v-bind="isExternal(link) ? { href: link, target: '_blank', rel: 'noopener noreferrer' } : { to: link }"
                      class="tm-review-link-left"
                    >
                      <span class="tm-review-link-icon" :class="isExternal(link) ? 'tm-review-link-icon--ext' : ''">
                        <svg v-if="isExternal(link)" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                        <svg v-else width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                      </span>
                      <span class="tm-review-link-path">{{ link }}</span>
                      <span v-if="isExternal(link)" class="tm-ext-badge">ext</span>
                    </component>
                    <div class="tm-review-link-actions">
                      <component
                        :is="isExternal(link) ? 'a' : 'router-link'"
                        v-bind="isExternal(link) ? { href: link, target: '_blank', rel: 'noopener noreferrer' } : { to: link }"
                        class="tm-view-btn"
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                        View
                      </component>
                      <button class="tm-del-link-btn" @click="removeReviewLink(selectedTask, link)" title="Remove">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  </div>
                </div>
                <button class="tm-add-link-inline-btn" @click="openReviewDialog(selectedTask)">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                  Add link or page
                </button>
              </div>

              <div v-if="!(taskComments[selectedTask.id] || []).length && !(selectedTask.review_links && selectedTask.review_links.length)" class="tm-comments-empty">No activity yet.</div>
              <div v-for="c in taskComments[selectedTask.id] || []" :key="c.id" class="tm-comment">
                <div class="tm-comment-head"><strong>{{ c.author }}</strong><span class="tm-msg-date">{{ formatRelativeDate(c.created_at) }}</span></div>
                <p>{{ c.content }}</p>
              </div>
              <div class="tm-comment-form">
                <textarea v-model="newComment" placeholder="Write a comment…" rows="2" class="tm-input tm-textarea"></textarea>
                <button class="tm-btn-primary tm-btn-primary--xs" :disabled="!newComment.trim() || sendingComment" @click="addComment">
                  <span v-if="sendingComment" class="tm-spin">⟳</span><span v-else>Send</span>
                </button>
              </div>
            </div>

            <div class="tm-drawer-actions">
              <button v-if="!selectedTask.completed && selectedTask.status !== 'review'" class="tm-btn-outline tm-btn-outline--violet" @click="openReviewDialog(selectedTask); detailsDrawer = false">Submit for review</button>
              <button class="tm-btn-outline tm-btn-outline--blue" @click="openEditDialog(selectedTask); detailsDrawer = false">Edit</button>
              <button class="tm-btn-outline tm-btn-outline--danger" @click="confirmDelete(selectedTask); detailsDrawer = false">Delete</button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Toast -->
    <transition name="tm-toast">
      <div v-if="toast.show" class="tm-toast" :class="'tm-toast--' + toast.type">
        <svg v-if="toast.type === 'success'" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>
        <svg v-else width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
        {{ toast.message }}
      </div>
    </transition>

  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'MonitorTasks',
  data() {
    return {
      tasks: [], filteredTasks: [],
      dialog: false, deleteDialog: false, detailsDrawer: false,
      subtaskDialog: false, reviewDialog: false,
      saving: false, deleting: false, savingSubtask: false,
      sendingComment: false, submittingReview: false,
      editingTask: null, selectedTask: null, taskToDelete: null,
      subtaskParent: null, reviewTask: null,
      selectedRoutes: [],
      customUrl: '',
      customUrls: [],
      routeSearch: '',
      reviewTab: 'routes',
      taskForm: { title: '', description: '', assigned_to: null, priority: 'medium' },
      subtaskForm: { title: '', description: '', assigned_to: null, priority: 'medium' },
      users: [], newComment: '',
      searchQuery: '', filterStatus: 'all', priorityFilter: 'all', sortBy: 'newest',
      toast: { show: false, message: '', type: 'success' },
      taskComments: {}, inlineReply: {}, sendingInline: {},
      currentUser: 'me'
    }
  },
  computed: {
    stats() {
      const total   = this.tasks.length
      const done    = this.tasks.filter(t => t.completed).length
      const review  = this.tasks.filter(t => t.status === 'review').length
      const pending = this.tasks.filter(t => !t.completed && t.status !== 'review').length
      return [
        { value: total }, { value: done }, { value: review }, { value: pending }
      ]
    },
    completionPercentage() {
      if (!this.tasks.length) return 0
      return Math.round((this.tasks.filter(t => t.completed).length / this.tasks.length) * 100)
    },
    filteredRoutes() {
      const all = this.getRoutes()
      if (!this.routeSearch.trim()) return all
      const q = this.routeSearch.toLowerCase()
      return all.filter(r => r.toLowerCase().includes(q))
    },
    // Combinaison routes cochées + custom URLs pour le submit
    allSelectedLinks() {
      return [...this.selectedRoutes, ...this.customUrls]
    }
  },
  watch: {
    searchQuery()    { this.filterTasks() },
    filterStatus()   { this.filterTasks() },
    priorityFilter() { this.filterTasks() },
    sortBy()         { this.sortTasks() }
  },
  mounted() { this.loadTasks(); this.loadUsers() },
  methods: {
    // ── Data ──────────────────────────────────────────────────────────
    async loadTasks() {
      try {
        const res = await axios.get('/api/v1/manager/monitor/tasks/')
        this.tasks = res.data
        this.tasks.forEach(t => { if (t.progress === undefined) t.progress = 0 })
        this.filterTasks()
        await this.loadAllComments()
      } catch { this.showToast('Failed to load tasks', 'error') }
    },
    async loadUsers() {
      try { const res = await axios.get('/api/v1/manager/managers/'); this.users = res.data } catch {}
    },
    async loadAllComments() {
      await Promise.all(this.tasks.map(async t => {
        try { const res = await axios.get(`/api/v1/manager/monitor/tasks/${t.id}/comments/`); this.$set(this.taskComments, t.id, res.data) }
        catch { this.$set(this.taskComments, t.id, []) }
      }))
    },

    // ── Inline comment ────────────────────────────────────────────────
    async sendInlineComment(taskId) {
      const content = (this.inlineReply[taskId] || '').trim()
      if (!content) return
      this.$set(this.sendingInline, taskId, true)
      try {
        const res = await axios.post(`/api/v1/manager/monitor/tasks/${taskId}/comments/`, { content })
        if (!this.taskComments[taskId]) this.$set(this.taskComments, taskId, [])
        this.taskComments[taskId].push(res.data)
        this.$set(this.inlineReply, taskId, '')
        this.showToast('Comment added', 'success')
      } catch { this.showToast('Failed to send', 'error') }
      finally { this.$set(this.sendingInline, taskId, false) }
    },

    // ── Filter / Sort ─────────────────────────────────────────────────
    filterTasks() {
      let f = [...this.tasks]
      if (this.searchQuery) { const q = this.searchQuery.toLowerCase(); f = f.filter(t => t.title.toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q)) }
      if (this.filterStatus === 'pending')      f = f.filter(t => !t.completed && t.status !== 'review')
      else if (this.filterStatus === 'in_progress') f = f.filter(t => t.status === 'in_progress')
      else if (this.filterStatus === 'review')  f = f.filter(t => t.status === 'review')
      else if (this.filterStatus === 'completed') f = f.filter(t => t.completed)
      if (this.priorityFilter !== 'all') f = f.filter(t => t.priority === this.priorityFilter)
      this.filteredTasks = f; this.sortTasks()
    },
    sortTasks() {
      const s = [...this.filteredTasks]
      const pOrd = { high: 0, medium: 1, low: 2 }
      if (this.sortBy === 'newest')   s.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      else if (this.sortBy === 'oldest')   s.sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
      else if (this.sortBy === 'priority') s.sort((a, b) => (pOrd[a.priority] ?? 9) - (pOrd[b.priority] ?? 9))
      this.filteredTasks = s
    },

    // ── Toggle ────────────────────────────────────────────────────────
    async toggleComplete(task) {
      try {
        const res = await axios.patch(`/api/v1/manager/monitor/tasks/${task.id}/`, { completed: !task.completed, status: !task.completed ? 'completed' : 'pending' })
        const i = this.tasks.findIndex(t => t.id === task.id)
        if (i !== -1) this.$set(this.tasks, i, { ...task, ...res.data })
        this.filterTasks()
        this.showToast(task.completed ? 'Task reopened' : 'Task completed!', 'success')
      } catch { this.showToast('Failed to update', 'error') }
    },

    // ── Review ────────────────────────────────────────────────────────
    openReviewDialog(task) {
      this.reviewTask     = task
      // Sépare les routes internes des URLs externes déjà sauvegardées
      const existing = task.review_links || []
      this.selectedRoutes = existing.filter(l => !this.isExternal(l))
      this.customUrls     = existing.filter(l => this.isExternal(l))
      this.customUrl      = ''
      this.routeSearch    = ''
      this.reviewTab      = 'routes'
      this.reviewDialog   = true
    },
    getRoutes() {
      const flatten = (routes, prefix = '') =>
        routes.flatMap(r => {
          const full = prefix + (r.path.startsWith('/') ? r.path : '/' + r.path)
          return [full, ...(r.children ? flatten(r.children, full) : [])]
        })
      return flatten(this.$router.options.routes).filter(p => p && p !== '*' && !p.includes(':') && !p.includes('*'))
    },
    isExternal(url) {
      return /^https?:\/\//i.test(url)
    },
    addCustomUrl() {
      const url = this.customUrl.trim()
      if (!url) return
      // Ajoute https:// si oublié
      const full = /^https?:\/\//i.test(url) ? url : 'https://' + url
      if (!this.customUrls.includes(full)) this.customUrls.push(full)
      this.customUrl = ''
    },
    removeFromSelection(link) {
      if (this.isExternal(link)) this.customUrls = this.customUrls.filter(u => u !== link)
      else this.selectedRoutes = this.selectedRoutes.filter(r => r !== link)
    },
    async markReview(task) {
      if (!task) return
      this.submittingReview = true
      const allLinks = this.allSelectedLinks
      try {
        const res = await axios.patch(`/api/v1/manager/monitor/tasks/${task.id}/`, { status: 'review', review_links: allLinks })
        const updated = { ...task, ...res.data, review_links: allLinks, status: 'review' }
        const i = this.tasks.findIndex(t => t.id === task.id)
        if (i !== -1) this.$set(this.tasks, i, updated)
        if (this.selectedTask && this.selectedTask.id === task.id) this.selectedTask = updated
        this.filterTasks()
        this.reviewDialog = false
        this.showToast('Submitted for review ✓', 'success')
      } catch { this.showToast('Failed to submit', 'error') }
      finally { this.submittingReview = false }
    },
    async removeReviewLink(task, link) {
      const newLinks = (task.review_links || []).filter(l => l !== link)
      try {
        const res = await axios.patch(`/api/v1/manager/monitor/tasks/${task.id}/`, { review_links: newLinks, ...(newLinks.length === 0 ? { status: 'pending' } : {}) })
        const updated = { ...task, ...res.data, review_links: newLinks, status: newLinks.length === 0 ? 'pending' : task.status }
        const i = this.tasks.findIndex(t => t.id === task.id)
        if (i !== -1) this.$set(this.tasks, i, updated)
        if (this.selectedTask && this.selectedTask.id === task.id) this.selectedTask = updated
        this.filterTasks()
        this.showToast('Link removed', 'success')
      } catch { this.showToast('Failed to remove', 'error') }
    },

    // ── Subtasks ──────────────────────────────────────────────────────
    openSubtaskDialog(task) { this.subtaskParent = task; this.subtaskForm = { title: '', description: '', assigned_to: null, priority: 'medium' }; this.subtaskDialog = true },
    async createSubtask() {
      if (!this.subtaskParent || !this.subtaskForm.title.trim()) return
      this.savingSubtask = true
      try {
        await axios.post(`/api/v1/manager/monitor/tasks/${this.subtaskParent.id}/subtasks/`, this.subtaskForm)
        this.showToast('Subtask created', 'success'); this.subtaskDialog = false
        await this.loadTasks()
        if (this.selectedTask && this.selectedTask.id === this.subtaskParent.id) this.selectedTask = this.tasks.find(t => t.id === this.selectedTask.id)
      } catch { this.showToast('Failed', 'error') }
      finally { this.savingSubtask = false }
    },

    // ── AI ────────────────────────────────────────────────────────────
    async fetchAIProgress(task) {
      try {
        const res = await axios.get(`/api/v1/manager/monitor/tasks/${task.id}/progress-ai/`)
        const progress = res.data.percentage
        const t = this.tasks.find(t => t.id === task.id); if (t) t.progress = progress
        if (this.selectedTask && this.selectedTask.id === task.id) this.selectedTask.progress = progress
        this.filterTasks(); this.showToast(`AI estimate: ${progress}%`, 'success')
      } catch { this.showToast('AI estimation failed', 'error') }
    },

    // ── Comments ──────────────────────────────────────────────────────
    async addComment() {
      if (!this.newComment.trim() || !this.selectedTask) return
      this.sendingComment = true
      try {
        const res = await axios.post(`/api/v1/manager/monitor/tasks/${this.selectedTask.id}/comments/`, { content: this.newComment })
        if (!this.taskComments[this.selectedTask.id]) this.$set(this.taskComments, this.selectedTask.id, [])
        this.taskComments[this.selectedTask.id].push(res.data)
        this.newComment = ''; this.showToast('Comment added', 'success')
      } catch { this.showToast('Failed', 'error') }
      finally { this.sendingComment = false }
    },

    // ── CRUD ──────────────────────────────────────────────────────────
    openCreateDialog() { this.editingTask = null; this.taskForm = { title: '', description: '', assigned_to: null, priority: 'medium' }; this.dialog = true },
    openEditDialog(task) {
      this.editingTask = task
      const u = this.users.find(u => u.username === task.assigned_to)
      this.taskForm = { title: task.title, description: task.description || '', assigned_to: u ? u.id : null, priority: task.priority || 'medium' }
      this.dialog = true
    },
    closeDialog() { this.dialog = false; this.editingTask = null },
    async saveTask() {
      this.saving = true
      try {
        if (this.editingTask) {
          const res = await axios.patch(`/api/v1/manager/monitor/tasks/${this.editingTask.id}/`, this.taskForm)
          const i = this.tasks.findIndex(t => t.id === this.editingTask.id)
          if (i !== -1) this.tasks.splice(i, 1, { ...this.editingTask, ...res.data })
          this.showToast('Task updated', 'success')
        } else {
          await axios.post('/api/v1/manager/monitor/tasks/', this.taskForm)
          this.showToast('Task created', 'success')
        }
        this.dialog = false; this.loadTasks()
      } catch { this.showToast('Failed', 'error') }
      finally { this.saving = false }
    },
    confirmDelete(task) { this.taskToDelete = task; this.deleteDialog = true },
    async deleteTask() {
      if (!this.taskToDelete) return; this.deleting = true
      try {
        await axios.delete(`/api/v1/manager/monitor/tasks/${this.taskToDelete.id}/`)
        this.tasks = this.tasks.filter(t => t.id !== this.taskToDelete.id); this.filterTasks(); this.showToast('Task deleted', 'success')
      } catch { this.showToast('Failed', 'error') }
      finally { this.deleting = false; this.deleteDialog = false; this.taskToDelete = null }
    },
    openTaskDetails(task) { this.selectedTask = task; this.newComment = ''; this.detailsDrawer = true },

    // ── Helpers ───────────────────────────────────────────────────────
    getUserName(id) { if (!id) return null; const u = this.users.find(u => u.id === id); return u ? u.username : id },
    getUserInitial(id) { const n = this.getUserName(id); return n ? n.charAt(0).toUpperCase() : '?' },
    statusLabel(s) { return { pending: 'Pending', in_progress: 'In Progress', review: 'In Review', completed: 'Completed' }[s] || s || 'Pending' },
    formatDate(d) { if (!d) return ''; return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) },
    formatRelativeDate(d) {
      if (!d) return ''
      const diff = Math.ceil((new Date(d) - new Date()) / 864e5)
      if (diff === 0) return 'Today'; if (diff === 1) return 'Tomorrow'; if (diff === -1) return 'Yesterday'
      if (diff > 0 && diff < 7) return `In ${diff}d`; if (diff < 0 && diff > -7) return `${Math.abs(diff)}d ago`
      return this.formatDate(d)
    },
    showToast(message, type = 'success') { this.toast = { show: true, message, type }; setTimeout(() => { this.toast.show = false }, 3000) }
  }
}
</script>

<style scoped>
*, *::before, *::after { box-sizing: border-box; }

.task-manager {
  padding: 14px; max-width: 900px; margin: 0 auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
  color: #1c2128; background: #f0f2f5; min-height: 100vh;
}

/* ── Header ── */
.tm-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; gap: 10px; }
.tm-header-left { display: flex; align-items: center; gap: 10px; min-width: 0; }
.tm-header-icon { width: 40px; height: 40px; border-radius: 11px; background: linear-gradient(135deg, #2da44e, #22863a); color: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 8px rgba(45,164,78,.35); }
.tm-header-title { font-size: 18px; font-weight: 700; color: #1c2128; margin: 0; }
.tm-header-sub { font-size: 12px; color: #57606a; margin: 1px 0 0; }

/* ── Buttons ── */
.tm-btn-primary { display: inline-flex; align-items: center; gap: 5px; background: #2da44e; color: #fff; border: none; border-radius: 8px; padding: 9px 16px; font-size: 13px; font-weight: 600; cursor: pointer; min-height: 40px; transition: background .15s, transform .1s; box-shadow: 0 1px 4px rgba(45,164,78,.3); }
.tm-btn-primary:hover { background: #2c974b; transform: translateY(-1px); }
.tm-btn-primary:active { transform: translateY(0); }
.tm-btn-primary:disabled { opacity: .6; cursor: not-allowed; transform: none; }
.tm-btn-primary--sm { min-height: 36px; padding: 7px 14px; }
.tm-btn-primary--xs { min-height: 32px; padding: 5px 12px; font-size: 12px; }
.tm-btn-ghost { display: inline-flex; align-items: center; gap: 5px; background: #fff; color: #57606a; border: 1px solid #d0d7de; border-radius: 8px; padding: 9px 14px; font-size: 13px; font-weight: 600; cursor: pointer; min-height: 40px; transition: background .15s; }
.tm-btn-ghost:hover { background: #f6f8fa; }
.tm-btn-danger { display: inline-flex; align-items: center; gap: 5px; background: #cf222e; color: #fff; border: none; border-radius: 8px; padding: 9px 14px; font-size: 13px; font-weight: 600; cursor: pointer; min-height: 40px; transition: background .15s; }
.tm-btn-danger:hover { background: #a40e26; }
.tm-btn-danger:disabled { opacity: .6; cursor: not-allowed; }
.tm-btn-violet { display: inline-flex; align-items: center; gap: 5px; background: #8250df; color: #fff; border: none; border-radius: 8px; padding: 9px 14px; font-size: 13px; font-weight: 600; cursor: pointer; min-height: 40px; transition: background .15s; white-space: nowrap; }
.tm-btn-violet:hover { background: #6e40c9; }
.tm-btn-violet:disabled { opacity: .6; cursor: not-allowed; }
.tm-btn-violet-solid { display: inline-flex; align-items: center; gap: 5px; background: #8250df; color: #fff; border: none; border-radius: 8px; padding: 9px 16px; font-size: 13px; font-weight: 600; cursor: pointer; min-height: 40px; transition: background .15s; }
.tm-btn-violet-solid:hover { background: #6e40c9; }
.tm-btn-violet-solid:disabled { opacity: .5; cursor: not-allowed; }
.tm-btn-outline { display: inline-flex; align-items: center; justify-content: center; padding: 9px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; cursor: pointer; min-height: 40px; transition: background .15s; }
.tm-btn-outline--violet { border: 1px solid #8250df; color: #8250df; background: #fff; }
.tm-btn-outline--violet:hover { background: #fbefff; }
.tm-btn-outline--blue { border: 1px solid #0969da; color: #0969da; background: #fff; }
.tm-btn-outline--blue:hover { background: #ddf4ff; }
.tm-btn-outline--danger { border: 1px solid #cf222e; color: #cf222e; background: #fff; }
.tm-btn-outline--danger:hover { background: #ffebe9; }

/* ── Stats ── */
.tm-stats { background: #fff; border: 1px solid #d0d7de; border-radius: 14px; padding: 14px; margin-bottom: 12px; box-shadow: 0 1px 4px rgba(0,0,0,.05); }
.tm-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 12px; }
.tm-stat { display: flex; flex-direction: column; align-items: center; padding: 10px 6px; border-radius: 10px; background: #f6f8fa; cursor: pointer; transition: background .15s, transform .1s; border: 1px solid transparent; }
.tm-stat:hover { background: #eaeef2; transform: translateY(-1px); }
.tm-stat-num { font-size: 22px; font-weight: 800; line-height: 1.1; }
.tm-stat-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: #57606a; margin-top: 3px; }
.tm-stat--total .tm-stat-num { color: #0969da; }
.tm-stat--done .tm-stat-num { color: #1a7f37; }
.tm-stat--review .tm-stat-num { color: #8250df; }
.tm-stat--pending .tm-stat-num { color: #cf222e; }
.tm-progress-wrap { display: flex; align-items: center; gap: 8px; }
.tm-progress-track { flex: 1; height: 7px; border-radius: 99px; background: #eaeef2; overflow: hidden; }
.tm-progress-fill { height: 100%; border-radius: 99px; background: linear-gradient(90deg, #2da44e, #1a7f37); transition: width .5s cubic-bezier(.4,0,.2,1); }
.tm-progress-text { font-size: 12px; font-weight: 700; color: #57606a; min-width: 32px; text-align: right; }

/* ── Toolbar ── */
.tm-toolbar { display: flex; gap: 8px; margin-bottom: 12px; align-items: center; }
.tm-search { position: relative; flex: 1; }
.tm-search-icon { position: absolute; left: 11px; top: 50%; transform: translateY(-50%); pointer-events: none; }
.tm-search-input { width: 100%; padding: 10px 10px 10px 34px; border: 1px solid #d0d7de; border-radius: 10px; font-size: 14px; background: #fff; color: #1c2128; outline: none; min-height: 42px; transition: border-color .15s, box-shadow .15s; }
.tm-search-input:focus { border-color: #0969da; box-shadow: 0 0 0 3px rgba(9,105,218,.12); }
.tm-toolbar-right { display: flex; gap: 6px; flex-shrink: 0; }
.tm-select-pill { padding: 8px 10px; border: 1px solid #d0d7de; border-radius: 10px; font-size: 12px; font-weight: 600; background: #fff; color: #57606a; outline: none; cursor: pointer; min-height: 42px; transition: border-color .15s; }
.tm-select-pill:focus { border-color: #0969da; }

/* ── Task list ── */
.tm-list { display: flex; flex-direction: column; gap: 6px; }
.tm-task-wrap { background: #fff; border: 1px solid #d0d7de; border-radius: 12px; overflow: hidden; transition: box-shadow .15s; }
.tm-task-wrap:hover { box-shadow: 0 2px 10px rgba(0,0,0,.08); }

.tm-item { display: flex; align-items: flex-start; gap: 10px; padding: 13px 12px; cursor: pointer; transition: background .12s; border-left: 3px solid transparent; }
.tm-item:hover { background: #fafbfc; }
.tm-item--high { border-left-color: #cf222e; }
.tm-item--medium { border-left-color: #d4a72c; }
.tm-item--low { border-left-color: #2da44e; }
.tm-item--done { opacity: .65; }
.tm-item--review { border-left-color: #8250df; background: #faf5ff; }
.tm-item--review:hover { background: #f3e8ff; }

.tm-check { background: none; border: none; padding: 4px; cursor: pointer; flex-shrink: 0; line-height: 0; border-radius: 50%; min-width: 34px; min-height: 34px; display: flex; align-items: center; justify-content: center; transition: background .15s, transform .12s; }
.tm-check:hover { background: #eaeef2; transform: scale(1.08); }

.tm-item-body { flex: 1; min-width: 0; }
.tm-item-top { display: flex; align-items: flex-start; flex-wrap: wrap; gap: 5px; margin-bottom: 4px; }
.tm-item-title { font-size: 14px; font-weight: 600; color: #1c2128; word-break: break-word; line-height: 1.4; flex: 1; min-width: 0; }
.tm-item-title--done { text-decoration: line-through; color: #8c959f; }
.tm-item-pills { display: flex; flex-wrap: wrap; gap: 4px; }
.tm-item-desc { font-size: 12px; color: #57606a; margin: 0 0 8px; word-break: break-word; line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.tm-item-desc--done { text-decoration: line-through; color: #afb8c1; }

.tm-item-foot { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; }
.tm-foot-chip { display: inline-flex; align-items: center; gap: 4px; font-size: 11px; font-weight: 500; color: #57606a; background: #f6f8fa; border: 1px solid #eaeef2; border-radius: 99px; padding: 2px 8px; line-height: 1.4; white-space: nowrap; }
.tm-foot-avatar { width: 14px; height: 14px; border-radius: 50%; background: #0969da; color: #fff; font-size: 8px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.tm-foot-chip--time { color: #8c959f; }
.tm-foot-chip--purple { color: #8250df; background: #fbefff; border-color: #e9d5ff; }
.tm-foot-chip--blue { color: #0969da; background: #ddf4ff; border-color: #b6e3ff; }
.tm-foot-chip--violet { color: #8250df; background: #fbefff; border-color: #d8b9f8; }
.tm-foot-chip--green { color: #1a7f37; background: #dafbe1; border-color: #aceebb; }

/* Pills (status/priority badges) */
.tm-pill { display: inline-flex; align-items: center; font-size: 10px; font-weight: 700; letter-spacing: .3px; text-transform: uppercase; padding: 2px 7px; border-radius: 99px; white-space: nowrap; }
.tm-pill--high { background: #ffebe9; color: #82071e; }
.tm-pill--medium { background: #fff8c5; color: #7d4e00; }
.tm-pill--low { background: #dafbe1; color: #116329; }
.tm-pill--pending { background: #eaeef2; color: #57606a; }
.tm-pill--in_progress { background: #ddf4ff; color: #0969da; }
.tm-pill--review { background: #fbefff; color: #8250df; }
.tm-pill--completed { background: #dafbe1; color: #116329; }
.tm-pill--none { background: #eaeef2; color: #57606a; }

/* Item actions */
.tm-item-actions { display: flex; flex-direction: column; gap: 2px; flex-shrink: 0; }
.tm-action { width: 32px; height: 32px; border-radius: 8px; border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: background .15s, color .15s; background: transparent; color: #8c959f; }
.tm-action:hover { background: #eaeef2; color: #1c2128; }
.tm-action--violet:hover { color: #8250df; background: #fbefff; }
.tm-action--blue:hover { color: #0969da; background: #ddf4ff; }
.tm-action--edit:hover { color: #0969da; background: #ddf4ff; }
.tm-action--del:hover { color: #82071e; background: #ffebe9; }

/* ================================================================
   THREAD & REVIEW BLOC
   ================================================================ */
.tm-thread { border-top: 1px solid #eaeef2; background: #fafbfc; }
.tm-thread-body { padding: 12px; }

/* Review bloc */
.tm-review-bloc { display: flex; gap: 9px; margin-bottom: 10px; }
.tm-review-bloc-avatar { width: 28px; height: 28px; border-radius: 50%; background: linear-gradient(135deg, #8250df, #6e40c9); color: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; box-shadow: 0 2px 6px rgba(130,80,223,.35); }
.tm-review-bloc-bubble { flex: 1; min-width: 0; border: 1px solid #d8b9f8; border-radius: 12px; border-top-left-radius: 3px; background: #fbefff; overflow: hidden; }
.tm-review-bloc-head { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; padding: 8px 12px; background: #f3e8ff; border-bottom: 1px solid #e9d5ff; }
.tm-review-bloc-author { font-size: 12px; font-weight: 700; color: #1c2128; }
.tm-review-tag { display: inline-flex; align-items: center; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .3px; background: #8250df; color: #fff; padding: 2px 7px; border-radius: 99px; }
.tm-review-bloc-date { font-size: 11px; color: #8c959f; margin-left: auto; }
.tm-review-bloc-sub { font-size: 11px; font-weight: 600; color: #6e40c9; margin: 8px 12px 6px; }

/* Links list */
.tm-review-links-list { display: flex; flex-direction: column; gap: 4px; padding: 0 10px; margin-bottom: 8px; }
.tm-review-link-row { display: flex; align-items: center; gap: 6px; padding: 6px 8px; background: #fff; border: 1px solid #e9d5ff; border-radius: 8px; transition: border-color .12s, background .12s; }
.tm-review-link-row:hover { border-color: #8250df; background: #faf5ff; }
.tm-review-link-left { display: flex; align-items: center; gap: 6px; min-width: 0; flex: 1; text-decoration: none; }
.tm-review-link-icon { width: 18px; height: 18px; border-radius: 4px; background: #ede9fe; display: flex; align-items: center; justify-content: center; flex-shrink: 0; color: #8250df; }
.tm-review-link-icon--ext { background: #fff3cd; color: #9a6700; }
.tm-review-link-path { font-size: 12px; font-weight: 600; color: #8250df; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tm-ext-badge { display: inline-flex; align-items: center; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .4px; background: #fff3cd; color: #7d4e00; padding: 1px 5px; border-radius: 4px; flex-shrink: 0; }
.tm-review-link-actions { display: flex; align-items: center; gap: 4px; flex-shrink: 0; }
.tm-view-btn { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; background: #8250df; color: #fff; border-radius: 5px; font-size: 11px; font-weight: 700; white-space: nowrap; text-decoration: none; transition: background .12s; }
.tm-view-btn:hover { background: #6e40c9; }
.tm-del-link-btn { width: 22px; height: 22px; border-radius: 5px; border: 1px solid #e9d5ff; background: #fff; color: #8250df; cursor: pointer; display: flex; align-items: center; justify-content: center; transition: all .12s; }
.tm-del-link-btn:hover { background: #ffebe9; border-color: #ffc1c1; color: #cf222e; }
.tm-add-link-inline-btn { display: inline-flex; align-items: center; gap: 5px; margin: 4px 10px 10px; padding: 5px 10px; border: 1px dashed #c4a8f5; border-radius: 6px; background: transparent; color: #8250df; font-size: 11px; font-weight: 600; cursor: pointer; transition: all .12s; }
.tm-add-link-inline-btn:hover { background: #f3e8ff; border-color: #8250df; }

/* Messages */
.tm-msg { display: flex; gap: 8px; margin-bottom: 10px; animation: fadeup .2s ease; }
@keyframes fadeup { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }
.tm-msg-avatar { width: 26px; height: 26px; border-radius: 50%; background: #0969da; color: #fff; font-size: 10px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; text-transform: uppercase; }
.tm-msg-avatar--me { background: #2da44e; }
.tm-msg-bubble { flex: 1; background: #fff; border: 1px solid #d0d7de; border-radius: 10px; border-top-left-radius: 2px; padding: 8px 10px; min-width: 0; }
.tm-msg-head { display: flex; align-items: center; gap: 6px; margin-bottom: 3px; }
.tm-msg-head strong { font-size: 12px; color: #1c2128; }
.tm-msg-date { font-size: 11px; color: #8c959f; margin-left: auto; }
.tm-msg-text { margin: 0; font-size: 13px; color: #1c2128; line-height: 1.5; word-break: break-word; }
.tm-reply { display: flex; gap: 8px; margin-top: 8px; }
.tm-reply-wrap { flex: 1; min-width: 0; }
.tm-reply-input { width: 100%; padding: 8px 10px; border: 1px solid #d0d7de; border-radius: 8px; font-size: 13px; font-family: inherit; background: #fff; color: #1c2128; outline: none; resize: none; min-height: 52px; transition: border-color .15s, box-shadow .15s; }
.tm-reply-input:focus { border-color: #8250df; box-shadow: 0 0 0 3px rgba(130,80,223,.1); }
.tm-reply-foot { display: flex; align-items: center; justify-content: space-between; margin-top: 5px; }
.tm-reply-hint { font-size: 11px; color: #afb8c1; }

/* ── Empty ── */
.tm-empty { text-align: center; padding: 56px 20px; background: #fff; border: 1px solid #d0d7de; border-radius: 14px; }
.tm-empty-icon { width: 56px; height: 56px; border-radius: 14px; background: #f6f8fa; display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; color: #8c959f; }
.tm-empty-title { font-size: 15px; font-weight: 700; color: #1c2128; margin-bottom: 6px; }
.tm-empty-sub { font-size: 13px; color: #8c959f; margin-bottom: 16px; }

/* ================================================================
   OVERLAYS / MODALS
   ================================================================ */
.tm-overlay { position: fixed; inset: 0; z-index: 1000; background: rgba(22,27,34,.6); display: flex; align-items: flex-end; backdrop-filter: blur(2px); }
.tm-overlay--drawer { align-items: flex-end; }
.tm-modal { background: #fff; border-radius: 18px 18px 0 0; width: 100%; max-height: 90vh; display: flex; flex-direction: column; box-shadow: 0 -12px 40px rgba(0,0,0,.18); overflow: hidden; border: 1px solid #d0d7de; }
.tm-modal--sm { max-width: 100%; }
.tm-modal--md { max-width: 100%; }
.tm-modal-header { display: flex; align-items: center; background: #1c2128; color: #fff; padding: 14px 16px; font-size: 14px; font-weight: 700; flex-shrink: 0; min-height: 52px; }
.tm-modal-header--danger { background: #cf222e; }
.tm-modal-header--violet { background: linear-gradient(135deg, #8250df, #6e40c9); }
.tm-modal-trunc { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
.tm-modal-close { margin-left: auto; background: none; border: none; color: rgba(255,255,255,.6); cursor: pointer; padding: 6px; line-height: 0; border-radius: 8px; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; transition: color .15s, background .15s; flex-shrink: 0; }
.tm-modal-close:hover { color: #fff; background: rgba(255,255,255,.15); }
.tm-modal-body { padding: 18px 16px; overflow-y: auto; flex: 1; }
.tm-modal-footer { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 16px; border-top: 1px solid #eaeef2; background: #f6f8fa; flex-shrink: 0; }

/* Form */
.tm-label { display: block; font-size: 13px; font-weight: 600; color: #1c2128; margin-bottom: 5px; }
.tm-label--mt { margin-top: 14px; }
.tm-req { color: #cf222e; }
.tm-input { display: block; width: 100%; padding: 10px 12px; border: 1px solid #d0d7de; border-radius: 9px; font-size: 14px; background: #fff; color: #1c2128; outline: none; font-family: inherit; line-height: 1.5; min-height: 44px; transition: border-color .15s, box-shadow .15s; -webkit-appearance: none; }
.tm-input:focus { border-color: #0969da; box-shadow: 0 0 0 3px rgba(9,105,218,.12); }
.tm-textarea { resize: vertical; min-height: 76px; }
.tm-sel { cursor: pointer; }
.tm-form-row { display: flex; gap: 10px; margin-top: 14px; }
.tm-form-col { flex: 1; min-width: 0; }
.tm-delete-icon { display: flex; justify-content: center; margin-bottom: 12px; }
.tm-delete-msg { font-size: 14px; line-height: 1.6; color: #1c2128; margin: 0; text-align: center; }

/* ================================================================
   REVIEW DIALOG
   ================================================================ */
.tm-review-tabs { display: flex; gap: 4px; background: #f0f2f5; border-radius: 10px; padding: 4px; margin-bottom: 14px; }
.tm-review-tab { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 8px; border: none; border-radius: 7px; background: transparent; color: #57606a; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; }
.tm-review-tab.active { background: #fff; color: #8250df; box-shadow: 0 1px 4px rgba(0,0,0,.1); }

.tm-rsearch { display: flex; align-items: center; gap: 8px; padding: 9px 12px; border: 1px solid #d0d7de; border-radius: 10px; background: #fff; margin-bottom: 10px; transition: border-color .15s, box-shadow .15s; }
.tm-rsearch:focus-within { border-color: #8250df; box-shadow: 0 0 0 3px rgba(130,80,223,.12); }
.tm-rsearch-input { flex: 1; border: none; outline: none; font-size: 14px; color: #1c2128; background: transparent; font-family: inherit; }
.tm-rsearch-input::placeholder { color: #8c959f; }
.tm-rsearch-clear { background: none; border: none; cursor: pointer; color: #8c959f; display: flex; align-items: center; justify-content: center; padding: 2px; border-radius: 4px; transition: color .12s; }
.tm-rsearch-clear:hover { color: #1c2128; }
.tm-route-list { display: flex; flex-direction: column; gap: 4px; max-height: 260px; overflow-y: auto; }
.tm-route-empty { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 24px; color: #8c959f; font-size: 13px; }
.tm-route-row { display: flex; align-items: center; gap: 8px; padding: 9px 12px; border: 1px solid #eaeef2; border-radius: 9px; cursor: pointer; font-size: 13px; font-weight: 500; color: #1c2128; transition: all .12s; user-select: none; }
.tm-route-row:hover { background: #f6f8fa; border-color: #d8b9f8; }
.tm-route-row--on { background: #fbefff; border-color: #8250df; color: #8250df; }
.tm-route-cb { accent-color: #8250df; width: 15px; height: 15px; flex-shrink: 0; }

/* Custom URL panel */
.tm-custom-url-panel { }
.tm-custom-url-hint { font-size: 12px; color: #57606a; margin: 0 0 12px; line-height: 1.5; }
.tm-custom-url-row { display: flex; gap: 8px; margin-bottom: 12px; }
.tm-custom-url-input-wrap { flex: 1; display: flex; align-items: center; gap: 8px; padding: 9px 12px; border: 1px solid #d0d7de; border-radius: 10px; background: #fff; transition: border-color .15s, box-shadow .15s; }
.tm-custom-url-input-wrap:focus-within { border-color: #8250df; box-shadow: 0 0 0 3px rgba(130,80,223,.12); }
.tm-custom-url-input { flex: 1; border: none; outline: none; font-size: 13px; color: #1c2128; background: transparent; font-family: inherit; min-width: 0; }
.tm-custom-url-input::placeholder { color: #8c959f; }
.tm-custom-url-list { display: flex; flex-direction: column; gap: 5px; max-height: 180px; overflow-y: auto; }
.tm-custom-url-item { display: flex; align-items: center; gap: 8px; padding: 8px 10px; background: #fff; border: 1px solid #d8b9f8; border-radius: 8px; }
.tm-custom-url-ext-badge { font-size: 9px; font-weight: 700; text-transform: uppercase; background: #fff3cd; color: #7d4e00; padding: 2px 5px; border-radius: 4px; flex-shrink: 0; }
.tm-custom-url-text { font-size: 12px; font-weight: 500; color: #8250df; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; flex: 1; }
.tm-custom-url-remove { background: none; border: none; cursor: pointer; color: #8c959f; display: flex; align-items: center; justify-content: center; width: 20px; height: 20px; border-radius: 4px; transition: color .12s, background .12s; flex-shrink: 0; }
.tm-custom-url-remove:hover { color: #cf222e; background: #ffebe9; }
.tm-custom-url-empty { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 24px; color: #8c959f; font-size: 13px; }

/* Selected summary */
.tm-selected-summary { margin-top: 14px; padding-top: 12px; border-top: 1px solid #eaeef2; }
.tm-selected-summary-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: #8c959f; margin-bottom: 8px; }
.tm-chips { display: flex; flex-wrap: wrap; gap: 5px; }
.tm-chip { display: inline-flex; align-items: center; gap: 5px; padding: 4px 8px; background: #fbefff; border: 1px solid #d8b9f8; border-radius: 99px; font-size: 11px; font-weight: 600; color: #8250df; max-width: 240px; }
.tm-chip-ext { font-size: 9px; font-weight: 700; text-transform: uppercase; background: #fff3cd; color: #7d4e00; padding: 1px 4px; border-radius: 3px; flex-shrink: 0; }
.tm-chip-x { background: none; border: none; cursor: pointer; color: #8250df; display: flex; align-items: center; padding: 1px; border-radius: 3px; transition: color .12s; opacity: .7; flex-shrink: 0; }
.tm-chip-x:hover { opacity: 1; color: #cf222e; }

/* ================================================================
   DRAWER
   ================================================================ */
.tm-drawer { position: fixed; bottom: 0; left: 0; right: 0; background: #fff; border-radius: 18px 18px 0 0; max-height: 88vh; display: flex; flex-direction: column; box-shadow: 0 -12px 40px rgba(0,0,0,.18); overflow: hidden; border-top: 1px solid #d0d7de; }
.tm-drawer-handle { width: 36px; height: 4px; background: #d0d7de; border-radius: 99px; margin: 10px auto 0; flex-shrink: 0; }
.tm-drawer-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid #eaeef2; flex-shrink: 0; }
.tm-drawer-title { font-size: 16px; font-weight: 700; color: #1c2128; }
.tm-drawer-close { background: none; border: none; cursor: pointer; color: #8c959f; padding: 6px; border-radius: 8px; line-height: 0; width: 34px; height: 34px; display: flex; align-items: center; justify-content: center; transition: color .15s, background .15s; }
.tm-drawer-close:hover { color: #1c2128; background: #eaeef2; }
.tm-drawer-body { padding: 16px; overflow-y: auto; flex: 1; }
.tm-drawer-pills { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 10px; }
.tm-drawer-title2 { font-size: 18px; font-weight: 700; color: #1c2128; margin: 0 0 6px; line-height: 1.3; }
.tm-drawer-desc { font-size: 14px; color: #57606a; margin: 0 0 16px; line-height: 1.6; }
.tm-detail-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 16px; }
.tm-detail-cell { background: #f6f8fa; border: 1px solid #eaeef2; border-radius: 9px; padding: 10px 12px; }
.tm-detail-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: #8c959f; display: block; margin-bottom: 4px; }
.tm-detail-val { font-size: 13px; font-weight: 600; color: #1c2128; word-break: break-word; display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.tm-ai-btn { background: #ddf4ff; border: 1px solid #b6e3ff; cursor: pointer; color: #0969da; padding: 1px 6px; border-radius: 5px; font-size: 11px; font-weight: 700; transition: background .15s; }
.tm-ai-btn:hover { background: #b6e3ff; }
.tm-section { margin-bottom: 16px; }
.tm-section-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .5px; color: #8c959f; display: block; margin-bottom: 8px; }
.tm-subtask-list { list-style: none; padding: 0; margin: 0; }
.tm-subtask-list li { font-size: 13px; padding: 7px 0; border-bottom: 1px solid #eaeef2; display: flex; align-items: center; }
.tm-strike { text-decoration: line-through; color: #8c959f; }

/* Comments */
.tm-comments { margin-top: 16px; border-top: 1px solid #eaeef2; padding-top: 14px; }
.tm-comments-head { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.tm-comments-count { background: #eaeef2; color: #57606a; font-size: 11px; border-radius: 99px; padding: 1px 8px; font-weight: 700; }
.tm-comments-empty { font-size: 13px; color: #8c959f; padding: 8px 0; }
.tm-comment { padding: 10px 0; border-bottom: 1px solid #eaeef2; }
.tm-comment-head { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.tm-comment-head strong { font-size: 13px; color: #1c2128; }
.tm-comment p { margin: 0; font-size: 14px; color: #1c2128; line-height: 1.5; }
.tm-comment-form { display: flex; flex-direction: column; gap: 8px; margin-top: 14px; }
.tm-comment-form .tm-btn-primary { align-self: flex-end; }
.tm-review-comment { background: #fbefff; border: 1px solid #d8b9f8; border-radius: 12px; padding: 12px; margin-bottom: 10px; }
.tm-review-comment-head { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 8px; }
.tm-review-comment-ava { width: 22px; height: 22px; border-radius: 50%; background: #8250df; color: #fff; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.tm-review-comment-sub { font-size: 11px; font-weight: 600; color: #6e40c9; margin: 0 0 8px; }
.tm-drawer-actions { display: flex; flex-direction: column; gap: 8px; margin-top: 20px; padding-top: 14px; border-top: 1px solid #eaeef2; }
.tm-drawer-actions .tm-btn-outline { width: 100%; }

/* ── Toast ── */
.tm-toast { position: fixed; bottom: 22px; left: 14px; right: 14px; max-width: 380px; margin: 0 auto; display: flex; align-items: center; gap: 8px; padding: 12px 16px; border-radius: 12px; font-size: 13px; font-weight: 600; box-shadow: 0 8px 28px rgba(0,0,0,.18); z-index: 2000; color: #fff; }
.tm-toast--success { background: linear-gradient(135deg, #1a7f37, #2da44e); }
.tm-toast--error { background: linear-gradient(135deg, #a40e26, #cf222e); }
.tm-toast--info { background: linear-gradient(135deg, #0550ae, #0969da); }

/* ── Transitions ── */
.tm-fade-enter-active, .tm-fade-leave-active { transition: opacity .2s ease; }
.tm-fade-enter, .tm-fade-leave-to { opacity: 0; }
.tm-slide-enter-active .tm-drawer, .tm-slide-leave-active .tm-drawer { transition: transform .3s cubic-bezier(.32,.72,0,1); }
.tm-slide-enter .tm-drawer, .tm-slide-leave-to .tm-drawer { transform: translateY(100%); }
.tm-toast-enter-active, .tm-toast-leave-active { transition: all .25s cubic-bezier(.32,.72,0,1); }
.tm-toast-enter, .tm-toast-leave-to { opacity: 0; transform: translateY(14px); }
.tm-thread-enter-active, .tm-thread-leave-active { transition: all .22s ease; overflow: hidden; }
.tm-thread-enter, .tm-thread-leave-to { opacity: 0; max-height: 0; }
.tm-thread-enter-to, .tm-thread-leave { opacity: 1; max-height: 800px; }
.tm-spin { animation: spin .8s linear infinite; display: inline-block; }
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Responsive ── */
@media (min-width: 540px) {
  .tm-overlay { align-items: center; }
  .tm-modal { border-radius: 14px; max-width: 520px; margin: auto; max-height: 88vh; }
  .tm-modal--md { max-width: 560px; }
  .tm-item-actions { opacity: 0; transition: opacity .15s; }
  .tm-item:hover .tm-item-actions { opacity: 1; }
  .tm-btn-label { display: inline; }
}
@media (min-width: 768px) {
  .task-manager { padding: 24px; }
  .tm-header-title { font-size: 20px; }
  .tm-item { padding: 14px 16px; }
  .tm-item-title { font-size: 15px; }
  .tm-item-desc { -webkit-line-clamp: unset; display: block; }
  .tm-drawer { max-width: 500px; left: auto; right: 0; border-radius: 0; border-left: 1px solid #d0d7de; max-height: 100vh; }
  .tm-drawer-actions { flex-direction: row; }
  .tm-drawer-actions .tm-btn-outline { width: auto; flex: 1; }
  .tm-detail-grid { grid-template-columns: 1fr 1fr 1fr; }
}
</style>
